import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { UserManager } from 'oidc-client-ts'
import * as Sentry from '@sentry/vue'

const userManager = new UserManager({
  authority:    import.meta.env.VITE_OIDC_AUTHORITY,
  client_id:    import.meta.env.VITE_OIDC_CLIENT_ID,
  redirect_uri: import.meta.env.VITE_OIDC_REDIRECT_URI,
  post_logout_redirect_uri: import.meta.env.VITE_OIDC_POST_LOGOUT_REDIRECT_URI,
  response_type: 'code',
  scope: 'openid profile email admin offline_access',
  automaticSilentRenew: true
})

export const useAuthStore = defineStore('auth', () => {
  const user = ref(null)
  const isAuthenticated = computed(() => !!user.value && !user.value.expired)
  const accessToken = computed(() => user.value?.access_token ?? null)

  // Attach the Authentik subject (id only — no PII) to Sentry events so issues
  // can be grouped by affected user. No-op when the SDK is disabled.
  function syncSentryUser(u) {
    if (u?.profile?.sub) {
      Sentry.setUser({ id: u.profile.sub })
    } else {
      Sentry.setUser(null)
    }
  }

  // Deduplicates concurrent silentRenew calls so that simultaneous 401 retries
  // don't race on the refresh token (Authentik rotates refresh tokens on use,
  // so two concurrent calls would invalidate each other).
  let renewPromise = null

  async function init() {
    user.value = await userManager.getUser()
    syncSentryUser(user.value)
    userManager.events.addUserLoaded(u => { user.value = u; syncSentryUser(u) })
    userManager.events.addUserUnloaded(() => { user.value = null; syncSentryUser(null) })
    userManager.events.addAccessTokenExpiring(() => silentRenew())
    userManager.events.addSilentRenewError(() => { user.value = null; syncSentryUser(null) })

    // If the stored token is already expired, attempt a silent renew immediately
    // so the user isn't shown as logged out after a period of inactivity.
    if (user.value?.expired) {
      await silentRenew()
    }
  }

  async function silentRenew() {
    if (renewPromise) return renewPromise
    renewPromise = userManager.signinSilent()
      .then(u => { user.value = u; syncSentryUser(u) })
      .catch(() => {
        // Authentik session has also expired — clear local state and let the
        // user sign in manually next time they hit a protected route.
        user.value = null
        syncSentryUser(null)
      })
      .finally(() => { renewPromise = null })
    return renewPromise
  }

  // Pass the current path so AuthCallbackView can redirect back after login.
  function login(redirectPath) {
    return userManager.signinRedirect(redirectPath ? { state: redirectPath } : undefined)
  }

  async function handleCallback() {
    const CALLBACK_TIMEOUT_MS = 15_000
    const result = await Promise.race([
      userManager.signinRedirectCallback(),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('OIDC callback timed out')), CALLBACK_TIMEOUT_MS)
      )
    ])
    user.value = result
    syncSentryUser(result)
    return result.state ?? null
  }

  function logout() {
    return userManager.signoutRedirect()
  }

  return { user, isAuthenticated, accessToken, init, login, handleCallback, logout, silentRenew }
})
